import React, { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import LandingPage from './pages/LandingPage';
import ClientPortal from './pages/ClientPortal';
import ClinicalDashboard from './pages/ClinicalDashboard';
import AdminPanel from './pages/AdminPanel';
import CleoIntake from './pages/CleoIntake';
import CleoScheduler from './pages/CleoScheduler';
import PrepPack from './pages/PrepPack';
import ApproachPage from './pages/ApproachPage';
import MembershipPage from './pages/MembershipPage';
import StoriesPage from './pages/StoriesPage';
import JournalPage from './pages/JournalPage';
import EmailGate from './pages/EmailGate';
import MagicLinkLogin from './pages/MagicLinkLogin';
import AuthVerify from './pages/AuthVerify';
import PathologyUpload from './pages/PathologyUpload';
import PathologyReview from './pages/PathologyReview';
import MyResults from './pages/MyResults';
import ProgramDashboard from './pages/ProgramDashboard';
import WeeklyModule from './pages/WeeklyModule';
import ClinicalCopilot from './pages/ClinicalCopilot';
import CleoScorePage from './pages/CleoScorePage';
import RetentionPage from './pages/RetentionPage';

// ── Chunk 3: Diagnostic experience pages ──
import PrepCurriculum from './pages/PrepCurriculum';
import PrepModule from './pages/PrepModule';
import ResultsReveal from './pages/ResultsReveal';
import ConsultationQuestions from './pages/ConsultationQuestions';
import CleoBrief from './pages/CleoBrief';

// ── Phase 2: Recommendation pipeline ──
import RecommendationBuilder from './pages/RecommendationBuilder';

// ── Phase 3: Patient enrolment ──
import PlanPreview from './pages/PlanPreview';
import PlanCheckout from './pages/PlanCheckout';
import EnrolmentSuccess from './pages/EnrolmentSuccess';

import { getCurrentUser, signOut, getBrowserClient } from './lib/supabase-browser';
import { VALID_PAGES } from './lib/valid-pages';
import './styles/tokens.css';

// ── Compute initial page from URL BEFORE first render ──

function getInitialPageFromUrl() {
  if (typeof window === 'undefined') return { page: 'landing', payload: {} };

  // Path-based route for the auth-verify page (target of magic-link
  // emails). The Vercel rewrite makes /auth/verify serve index.html;
  // this branch recognises we're on it and reads ?token=... into the
  // payload before App.jsx's URL_OVERRIDE_ACTIVE block strips the query
  // string. Token then lives in React state for AuthVerify to consume
  // on user button click.
  if (window.location.pathname === '/auth/verify') {
    const verifyParams = new URLSearchParams(window.location.search);
    const verifyPayload = { token: verifyParams.get('token') };
    // returnTo arrives via the email URL: /auth/verify?token=xxx&return=program
    // Client double-validates against VALID_RETURN_TO_PAGES before navigation;
    // server already validated before embedding, this is belt-and-braces.
    if (verifyParams.get('return')) verifyPayload.returnTo = verifyParams.get('return');
    return { page: 'auth-verify', payload: verifyPayload };
  }

  const params = new URLSearchParams(window.location.search);
  const p = params.get('page');
  if (!p || VALID_PAGES.indexOf(p) === -1) return { page: 'landing', payload: {} };
  const payload = {};
  const moduleSeq = params.get('module');
  if (moduleSeq) payload.moduleSequenceNumber = Number(moduleSeq);
  const briefIdParam = params.get('brief');
  if (briefIdParam) payload.briefId = briefIdParam;
  const recIdParam = params.get('recommendation');
  if (recIdParam) payload.recommendationId = recIdParam;
  // DEV-only override: lets PlanPreview be hit by the admin-seeded test
  // fixture without going through Supabase auth. The URL is generated by
  // /api/admin-seed-test-enrolment. Safe to keep in prod because the test
  // email only matches a fixture row; non-fixture queries 404.
  const emailParam = params.get('email');
  if (emailParam) payload.devEmail = emailParam;
  return { page: p, payload };
}

const INITIAL = getInitialPageFromUrl();
const URL_OVERRIDE_ACTIVE = INITIAL.page !== 'landing';

// ── Server-side admin role check ──
// Returns true if /api/check-admin says is_admin = true, false otherwise.
async function checkAdminStatus(email) {
  if (!email) return false;
  try {
    const r = await fetch('/api/check-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    });
    const data = await r.json();
    return data && data.isAdmin === true;
  } catch (e) {
    return false;
  }
}

export default function App() {
  const [page, setPage] = useState(INITIAL.page);
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [intakeId, setIntakeId] = useState(null);
  const [patientEmail, setPatientEmail] = useState(null);
  const [patientFirstName, setPatientFirstName] = useState(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [copilotPatientId, setCopilotPatientId] = useState(null);
  const [routePayload, setRoutePayload] = useState(INITIAL.payload);

  useEffect(() => {
    let mounted = true;

    // On mount: resolve current session AND admin status in parallel.
    getCurrentUser().then((u) => {
      if (!mounted) return;
      setUser(u);
      if (u && u.email) {
        setPatientEmail(u.email);
        checkAdminStatus(u.email).then((admin) => {
          if (mounted) setIsAdmin(admin);
        });
      }
    });

    // Clean URL once consumed (cosmetic)
    if (URL_OVERRIDE_ACTIVE) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    const supabase = getBrowserClient();
    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          if (session.user.email) {
            setPatientEmail(session.user.email);
            // Check admin status → route to /admin if admin, /portal otherwise.
            // Respect URL override (e.g. signing in directly on a deep link).
            checkAdminStatus(session.user.email).then((admin) => {
              if (!mounted) return;
              setIsAdmin(admin);
              if (URL_OVERRIDE_ACTIVE) return; // user intended a specific page
              setPage(admin ? 'admin' : 'portal');
            });
          }
        }
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setPatientEmail(null);
          setIsAdmin(false);
          setPage('landing');
        }
      });

      return () => {
        mounted = false;
        if (authListener?.subscription) authListener.subscription.unsubscribe();
      };
    }

    return () => { mounted = false; };
  }, []);

  const navigate = (dest, payload) => {
    if (dest === 'copilot' && payload?.patientId) {
      setCopilotPatientId(payload.patientId);
    }
    setRoutePayload(payload || {});
    setPage(dest);
    window.scrollTo(0, 0);
  };

  const handleEmailSubmit = (data) => {
    setPatientEmail(data.email);
    setPatientFirstName(data.firstName);
    navigate('intake');
  };

  const handleIntakeComplete = (data) => {
    setClinicalSummary(data.summary);
    if (data.intakeId) setIntakeId(data.intakeId);
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
    setPatientEmail(null);
    setPatientFirstName(null);
    setIsAdmin(false);
    navigate('landing');
  };

  const startIntake = () => {
    if (patientEmail) navigate('intake');
    else navigate('email-gate');
  };

  const handleNavigate = (dest, payload) => {
    if (dest === 'intake' || dest === 'audit') startIntake();
    else if (dest === 'signout') handleSignOut();
    else navigate(dest, payload);
  };

  return (
    // overflowX:hidden here (not just on body) prevents Navigation's flex
    // children — which overflow horizontally on narrow widths because the
    // bar is non-responsive — from extending the document's layout width
    // past the viewport. Without this, page-level <main> children inherit
    // a wider-than-viewport parent and content is left-anchored inside the
    // overflow rather than centred in the visible area. Belongs on the
    // shell wrapper, not on individual page components.
    <div style={{ minHeight: '100vh', background: '#1A1614', overflowX: 'hidden' }}>
      <Navigation onNavigate={handleNavigate} currentPage={page} user={user} isAdmin={isAdmin} />

      <main>
        {page === 'landing' && <LandingPage onNavigate={handleNavigate} />}
        {page === 'about' && <ApproachPage onNavigate={navigate} />}
        {page === 'membership' && <MembershipPage onNavigate={navigate} />}
        {page === 'stories' && <StoriesPage onNavigate={navigate} />}
        {page === 'journal' && <JournalPage onNavigate={navigate} />}

        {page === 'login' && <MagicLinkLogin onNavigate={navigate} routePayload={routePayload} />}

        {page === 'auth-verify' && (
          <AuthVerify onNavigate={navigate} token={routePayload.token} routePayload={routePayload} />
        )}

        {page === 'portal' && <ClientPortal onNavigate={handleNavigate} user={user} email={patientEmail} />}
        {page === 'clinical' && <ClinicalDashboard onNavigate={handleNavigate} email={patientEmail} isAdmin={isAdmin} />}
        {page === 'admin' && <AdminPanel onNavigate={navigate} email={patientEmail} isAdmin={isAdmin} />}
        {page === 'pathology-review' && <PathologyReview onNavigate={navigate} email={patientEmail} isAdmin={isAdmin} />}
        {page === 'copilot' && <ClinicalCopilot onNavigate={navigate} patientId={copilotPatientId} email={patientEmail} isAdmin={isAdmin} />}
        {page === 'cleo-score' && <CleoScorePage onNavigate={navigate} email={patientEmail} />}
        {page === 'program' && <ProgramDashboard onNavigate={navigate} email={patientEmail} />}
        {page === 'weekly-module' && <WeeklyModule onNavigate={navigate} email={patientEmail} routePayload={routePayload} />}
        {page === 'retention' && <RetentionPage onNavigate={navigate} email={patientEmail} isAdmin={isAdmin} />}

        {page === 'email-gate' && <EmailGate onSubmit={handleEmailSubmit} />}

        {page === 'pathology-upload' && (
          <PathologyUpload onNavigate={navigate} email={patientEmail} />
        )}

        {page === 'my-results' && (
          <MyResults onNavigate={navigate} email={patientEmail} />
        )}

        {page === 'intake' && (
          <CleoIntake
            onNavigate={navigate}
            onComplete={handleIntakeComplete}
            email={patientEmail}
            firstName={patientFirstName}
          />
        )}

        {page === 'scheduler' && (
          <CleoScheduler onNavigate={navigate} email={patientEmail} />
        )}

        {page === 'preppack' && (
          <PrepPack
            summary={clinicalSummary}
            intakeId={intakeId}
            email={patientEmail}
            onNavigate={navigate}
          />
        )}

        {/* ────────────────────────────────────────────────────────────
            CHUNK 3 — Diagnostic Experience pages
            ──────────────────────────────────────────────────────────── */}

        {page === 'prep-curriculum' && (
          <PrepCurriculum
            onNavigate={navigate}
            email={patientEmail}
            firstName={patientFirstName}
          />
        )}

        {page === 'prep-module' && (
          <PrepModule
            onNavigate={navigate}
            email={patientEmail}
            moduleSequenceNumber={routePayload.moduleSequenceNumber}
          />
        )}

        {page === 'results-reveal' && (
          <ResultsReveal
            onNavigate={navigate}
            email={patientEmail}
            firstName={patientFirstName}
          />
        )}

        {page === 'consultation-questions' && (
          <ConsultationQuestions
            onNavigate={navigate}
            email={patientEmail}
            firstName={patientFirstName}
          />
        )}

        {page === 'cleo-brief' && (
          <CleoBrief
            onNavigate={navigate}
            email={patientEmail}
            briefId={routePayload.briefId}
          />
        )}

        {page === 'recommendation-builder' && (
          <RecommendationBuilder
            onNavigate={navigate}
            recommendationId={routePayload.recommendationId}
            email={patientEmail}
            isAdmin={isAdmin}
          />
        )}

        {/* ────────────────────────────────────────────────────────────
            PHASE 3 — Patient enrolment
            ──────────────────────────────────────────────────────────── */}

        {page === 'plan-preview' && (
          <PlanPreview
            onNavigate={navigate}
            email={patientEmail}
            devEmail={routePayload.devEmail}
            redirectMessage={routePayload.redirectMessage}
          />
        )}

        {page === 'plan-checkout' && (
          <PlanCheckout
            onNavigate={navigate}
            email={patientEmail}
            devEmail={routePayload.devEmail}
            recommendationId={routePayload.recommendationId}
            packageId={routePayload.packageId}
            paymentOption={routePayload.paymentOption}
            serviceEdits={routePayload.serviceEdits}
            addedServices={routePayload.addedServices}
          />
        )}

        {page === 'enrolment-success' && (
          <EnrolmentSuccess
            onNavigate={navigate}
            email={patientEmail}
            devEmail={routePayload.devEmail}
            plan={routePayload.plan}
            package={routePayload.package}
            services={routePayload.services}
            scheduledPayments={routePayload.scheduledPayments}
            membership={routePayload.membership}
          />
        )}

      </main>
    </div>
  );
}
